import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  FiberManualRecord,
  LinkedIn,
  GitHub,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";

import vipinImg from "../../../images/vipinkvImg.jpeg";
import vigneshImg from "../../../images/vigneshImg.jpg";

const TEAM_MEMBERS = [
  {
    id: 1,
    name: "VIPIN K V",
    title: "Founder & C.E.O",
    image: vipinImg,
    gradient: ["#c1e328", "#a0c020"],
    linkedin: "https://www.linkedin.com/in/vipin-k-v/",
    github: "https://github.com/thinkervipin",
  },
  {
    id: 2,
    name: "VIGNESH MURUGAN",
    title: "Co-Founder & C.O.O",
    image: vigneshImg,
    gradient: ["#d0f030", "#c1e328"],
    linkedin: "https://www.linkedin.com/in/vignesh-murugan-582471259/",
  },
  // {
  //   id: 3,
  //   name: "AMEEN NOUSHAD",
  //   title: "Business Head & Web Developer",
  //   image: ameenImg,
  //   gradient: ["#e4ff3a", "#c1e328"],
  //   linkedin: "https://www.linkedin.com/in/ameen-noushad-888b79242/",
  //   github: "https://github.com/Ameen-Here",
  // },
];

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Styled Components
const BackgroundBox = styled(Box)({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "40px",
  paddingBottom: "40px",
});

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.05,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: "50px 50px",
  pointerEvents: "none",
});

const TeamCard = styled(Card)(({ theme, gradient }) => ({
  height: "100%",
  background: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(20px)",
  borderRadius: 24,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `linear-gradient(135deg, ${gradient[0]}15 0%, ${gradient[1]}15 100%)`,
    opacity: 0,
    transition: "opacity 0.6s ease",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    padding: "1px",
    background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    maskComposite: "exclude",
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    opacity: 0,
    transition: "opacity 0.6s ease",
  },
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: `${gradient[0]}60`,
    boxShadow: `0 8px 24px ${gradient[0]}25`,
    "&::before": {
      opacity: 0.6,
    },
    "&::after": {
      opacity: 0.7,
    },
    "& .team-avatar": {
      transform: "scale(1.03)",
      boxShadow: `0 8px 20px ${gradient[0]}30`,
    },
    "& .social-icons": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const TeamAvatar = styled(Avatar)(({ gradient }) => ({
  width: 200,
  height: 200,
  margin: "0 auto",
  border: `4px solid transparent`,
  background: `linear-gradient(#000000, #000000) padding-box,
               linear-gradient(135deg, ${gradient[0]}, ${gradient[1]}) border-box`,
  transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -8,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
    filter: "blur(20px)",
    opacity: 0.5,
    zIndex: -1,
  },
}));

const SocialIconButton = styled(IconButton)(({ gradient }) => ({
  width: 44,
  height: 44,
  background: "rgba(0, 0, 0, 0.8)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(193, 227, 40, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
    borderColor: "transparent",
    transform: "translateY(-2px) scale(1.05)",
    boxShadow: `0 4px 12px ${gradient[0]}40`,
  },
}));

const GradientText = styled(Typography)(({ gradient }) => ({
  background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 700,
}));

const RoleChip = styled(Chip)(({ gradient }) => ({
  background: `linear-gradient(135deg, ${gradient[0]}20, ${gradient[1]}20)`,
  border: `1px solid ${gradient[0]}40`,
  backdropFilter: "blur(8px)",
  color: gradient[0],
  fontWeight: 600,
  fontSize: "0.75rem",
  letterSpacing: 0.5,
  height: 28,
}));

const ShimmerLine = styled(Box)(({ gradient }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: 3,
  background: `linear-gradient(90deg, transparent, ${gradient[0]}, ${gradient[1]}, transparent)`,
  backgroundSize: "1000px 100%",
  animation: `${shimmer} 3s linear infinite`,
  opacity: 0,
  transition: "opacity 0.6s ease",
  ".MuiCard-root:hover &": {
    opacity: 1,
  },
}));

const TeamMemberCard = ({ member, index }) => {
  return (
    <Box
      sx={{
        animation: `${float} 3s ease-in-out infinite`,
        animationDelay: `${index * 0.2}s`,
      }}
    >
      <TeamCard
        gradient={member.gradient}
        elevation={0}
      >
        <CardContent
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Avatar */}
          <Box sx={{ mb: 3, position: "relative" }}>
            <TeamAvatar
              className="team-avatar"
              gradient={member.gradient}
              src={member.image}
              alt={member.name}
            />
            
            {/* Floating Badge */}
            {/* <Box
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${member.gradient[0]}, ${member.gradient[1]})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "3px solid #000000",
                boxShadow: `0 4px 12px ${member.gradient[0]}60`,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            >
              <FiberManualRecord sx={{ fontSize: 12, color: "#fff" }} />
            </Box> */}
          </Box>

          {/* Name */}
          <GradientText
            variant="h5"
            gradient={member.gradient}
            sx={{ mb: 1.5, textAlign: "center", fontSize: "1.5rem" }}
          >
            {member.name}
          </GradientText>

          {/* Role */}
          <RoleChip
            label={member.title}
            gradient={member.gradient}
            size="small"
            sx={{ mb: 3 }}
          />

          {/* Social Icons */}
          <Stack
            direction="row"
            spacing={2}
            className="social-icons"
            sx={{
              opacity: 0,
              transform: "translateY(10px)",
              transition: "all 0.4s ease 0.1s",
            }}
          >
            {member.linkedin && (
              <SocialIconButton
                gradient={member.gradient}
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedIn sx={{ fontSize: 20, color: "#fff" }} />
              </SocialIconButton>
            )}
            {member.github && (
              <SocialIconButton
                gradient={member.gradient}
                href={member.github}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GitHub sx={{ fontSize: 20, color: "#fff" }} />
              </SocialIconButton>
            )}
          </Stack>
        </CardContent>

        {/* Shimmer Effect on Hover */}
        <ShimmerLine gradient={member.gradient} />
      </TeamCard>
    </Box>
  );
};

const HomepageTeamNew = () => {
  return (
    <BackgroundBox>
      {/* Background Elements */}
      <GradientOrb top="33%" left={-256} color="rgba(193, 227, 40, 0.1)" />
      <GradientOrb bottom="33%" right={-256} color="rgba(193, 227, 40, 0.1)" />
      <GridPattern />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        {/* Section Header */}
        <Box textAlign="center" mb={10}>
          {/* Badge */}
          <Chip
            icon={
              <FiberManualRecord
                sx={{
                  fontSize: 8,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
            }
            label="LEADERSHIP TEAM"
            sx={{
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(193, 227, 40, 0.3)",
              color: "#c1e328",
              fontWeight: 700,
              letterSpacing: 3.2,
              fontSize: "0.555rem",
              mb: 3,
            }}
          />

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              fontWeight: 700,
              color: "#fff",
              letterSpacing: -1,
              mb: 2,
            }}
          >
            Meet Our
           {' '}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(135deg, #c1e328 0%, #b5d028 50%, #a8c728 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Founders
            </Box>
          </Typography>

          {/* Subtitle */}
          {/* <Typography
            variant="h6"
            sx={{
              color: "rgba(148, 163, 184, 1)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            The innovative minds driving the future of autonomous robotics
            and sustainable agriculture
          </Typography> */}
        </Box>

        {/* Team Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(2, 1fr)",
            },
            gap: 4,
            maxWidth: 900,
            mx: "auto",
          }}
        >
          {TEAM_MEMBERS.map((member, index) => (
            <TeamMemberCard key={member.id} member={member} index={index} />
          ))}
        </Box>

        
      </Container>
    </BackgroundBox>
  );
};

export default HomepageTeamNew;

